import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap';
import { Search, X } from 'lucide-react';
import { IdBadge, IdBadgeType } from '../../../../components/ui/badge/IdBadge';
import { skinSeoulProductsClient } from '../../../../infrastructure/SkinSeoulProductsClient';
import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';
import { PublicationStatus } from '../../../../models/ProductTypes';
import type { SkuPickerSelection } from '../utils/tree';

interface SkuPickerProps {
  show: boolean;
  initialQuery?: string;
  onSelect: (selection: SkuPickerSelection) => void;
  onClose: () => void;
}

const RESULT_LIMIT = 20;

export const SkuPicker: React.FC<SkuPickerProps> = ({ show, initialQuery = '', onSelect, onClose }) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SkinSeoulAdminProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) setQuery(initialQuery);
  }, [show, initialQuery]);

  useEffect(() => {
    if (!show) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      void (async () => {
        try {
          const isNumericLikely = /^\d+$/.test(trimmed);
          const products = isNumericLikely
            ? await skinSeoulProductsClient.searchProductsBySku(trimmed, PublicationStatus.PUBLISH)
            : (
                await skinSeoulProductsClient.getAllProducts(
                  1,
                  RESULT_LIMIT,
                  PublicationStatus.PUBLISH,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  trimmed,
                )
              ).products;
          if (cancelled) return;
          setResults(products);
        } catch (searchError) {
          if (cancelled) return;
          setError(searchError instanceof Error ? searchError.message : 'Search failed');
          setResults([]);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [show, query]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
  };

  const helpText = useMemo(() => {
    if (query.trim().length < 2) return 'Type the full SKU (digits) for an exact match, or part of the product name to search.';
    if (loading) return 'Searching…';
    if (results.length === 0) {
      if (/^\d+$/.test(query.trim())) return 'No product matches that SKU. SKUs require an exact match — try the full code or search by name.';
      return 'No products match this search.';
    }
    return `${results.length} product${results.length === 1 ? '' : 's'} found.`;
  }, [query, loading, results]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="h5 mb-0">Pick a product</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputGroup className="mb-3">
          <InputGroup.Text><Search size={14} /></InputGroup.Text>
          <Form.Control
            autoFocus
            placeholder="Search by SKU or product name…"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
          />
          {query && (
            <Button variant="outline-secondary" onClick={() => handleQueryChange('')} title="Clear">
              <X size={14} />
            </Button>
          )}
        </InputGroup>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <div className="text-muted small mb-2">{helpText}</div>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : results.length === 0 ? null : (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <Table hover bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '140px', fontSize: '0.75rem' }}>SKU</th>
                  <th style={{ fontSize: '0.75rem' }}>Product Name</th>
                  <th style={{ width: '90px', fontSize: '0.75rem', textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {results.map((product) => (
                  <tr key={product.productId} style={{ verticalAlign: 'middle' }}>
                    <td style={{ padding: '6px' }}>
                      <IdBadge type={IdBadgeType.SKU} value={product.sku} size="sm" showCopyIcon={false} />
                    </td>
                    <td style={{ padding: '6px', fontSize: '0.85rem' }}>{product.name}</td>
                    <td style={{ padding: '6px', textAlign: 'right' }}>
                      <Button
                        size="sm"
                        variant="dark"
                        onClick={() => onSelect({ sku: product.sku, productName: product.name })}
                      >
                        Pick
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
};
